/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ComponentType, useCallback, useEffect, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';

import { type NewCoreIconProps } from '@atlaskit/icon';
import { Ati } from '@atlassian/ari';
import type { SuggestionItemResponse } from '@atlassian/navigation-bff-data-provider';

import { useMenuControl } from '../../../context';
import { useUniversalCreateAnalytics } from '../../../hooks';
import { iconLoader } from '../../../services';
import type { AllowedIcon, MenuItem } from '../../../types';
import MenuContentItem from '../MenuItem';

export interface SuggestionItemBaseProps {
	/**
	 * Optional configuration to override the default menu item properties
	 * Note: isLoading will be combined with the internal icon loading state
	 */
	customisedItemConfig?: Partial<MenuItem>;
	/**
	 * The index of this item within the suggestion section (0-based)
	 */
	itemIndex: number;
	/**
	 * The response item from Universal Create
	 */
	responseItem: SuggestionItemResponse;
	/**
	 * The total count of items in the suggestion section
	 */
	totalItemsCount: number;
}

const noop = () => {};

/**
 * Suggestion item base component that renders a suggestion item in the Universal Create interface.
 * Handles dynamic icon loading and loading states.
 */
export function SuggestionItemBase({
	responseItem,
	customisedItemConfig,
	itemIndex,
	totalItemsCount,
}: SuggestionItemBaseProps) {
	const { suggestionConfig } = useMenuControl();
	const { fireNewSuggestionsMenuItemClicked } = useUniversalCreateAnalytics();

	const onClick = useCallback(() => {
		const onItemClick = (suggestionConfig?.enabled && suggestionConfig?.onItemClick) || noop;

		fireNewSuggestionsMenuItemClicked({
			ati: responseItem.ati,
			groupItemProduct: responseItem.product,
			groupItemsCount: totalItemsCount,
			groupItemsIndex: itemIndex,
			isRightClick: false,
			menuItemIndex: itemIndex,
			menuItemsCount: totalItemsCount,
		});

		// TODO: add skip condition in the future for platform items, such as goal, project or team forms.
		onItemClick?.(responseItem.ati);
	}, [
		suggestionConfig,
		responseItem,
		fireNewSuggestionsMenuItemClicked,
		itemIndex,
		totalItemsCount,
	]);

	const [icon, setIcon] = useState<ComponentType<NewCoreIconProps> | undefined>(undefined);
	const [isIconLoading, setIsIconLoading] = useState(false);
	const { isLoading: isCustomisedItemConfigLoading, ...restCustomisedItemConfig } =
		customisedItemConfig || {};

	const onRightClick = useCallback(
		(event: React.MouseEvent) => {
			// Fire analytics first
			fireNewSuggestionsMenuItemClicked({
				ati: responseItem.ati,
				groupItemProduct: responseItem.product,
				groupItemsCount: totalItemsCount,
				groupItemsIndex: itemIndex,
				isRightClick: true,
				menuItemIndex: itemIndex,
				menuItemsCount: totalItemsCount,
			});

			// Then call custom onContextMenu handler if provided
			const customOnContextMenu = restCustomisedItemConfig?.onContextMenu;
			customOnContextMenu?.(event);
		},
		[
			fireNewSuggestionsMenuItemClicked,
			responseItem,
			totalItemsCount,
			itemIndex,
			restCustomisedItemConfig?.onContextMenu,
		],
	);

	useEffect(
		function loadIcon() {
			if (!responseItem.icon) {
				setIcon(undefined);
				setIsIconLoading(false);
				return;
			}

			setIsIconLoading(true);

			iconLoader(responseItem.icon as AllowedIcon).then((loadedIcon) => {
				if (loadedIcon) {
					setIcon(() => loadedIcon);
				} else {
					setIcon(undefined);
				}
				setIsIconLoading(false);
			});
		},
		[responseItem.icon],
	);

	// Extract custom onClick handler and compose with analytics onClick
	const customOnClick = restCustomisedItemConfig?.onClick;

	const composedOnClick = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			// Fire analytics first
			onClick();
			// Then call custom click handler if provided
			customOnClick?.(event);
		},
		[customOnClick, onClick],
	);

	const menuItem: MenuItem = useMemo(() => {
		const { onClick: _, onContextMenu: __, ...otherCustomConfig } = restCustomisedItemConfig || {};

		return {
			ati: Ati.parse(responseItem.ati),
			children: responseItem.name,
			elemBefore: icon,
			isLoading: isIconLoading || isCustomisedItemConfigLoading,
			testId: `suggestion-item-${responseItem.ati}`,
			onClick: customOnClick ? composedOnClick : onClick,
			onContextMenu: onRightClick,
			...otherCustomConfig,
		};
	}, [
		responseItem.ati,
		responseItem.name,
		icon,
		isIconLoading,
		isCustomisedItemConfigLoading,
		onClick,
		composedOnClick,
		customOnClick,
		restCustomisedItemConfig,
		onRightClick,
	]);

	return <MenuContentItem item={menuItem} />;
}
